/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { css } from "@emotion/react";
import type { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type {
  ChallengeItemSectionItem,
  ChallengeSectionItem,
  CollectionSectionItem,
} from "@packages/sdk";
import type { SectionItemCollection } from "@packages/sdk/src/lib/types/schema/collection/sectionItemCollectionSchema";
import type { ReactNode } from "react";

import { spacing } from "../../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import {
  getChallengeStatus,
  getUsersCountLabel,
} from "../../../../../utils/content";
import { ChallengeCTAButton } from "../../../../blocks/primitives/refactor/ChallengeCTAButton/ChallengeCTAButton.emotion";
import { Button } from "../../../_core/Button/Button.emotion";
import { CheckmarkIcon } from "../../../icons/Icon/GeneralIcons";
import { ChallengeCheckmark } from "../../challenges/ChallengeCheckmark/ChallengeCheckmark.emotion";
import type { ChallengeLargeProps } from "../../challenges/ChallengeLarge/ChallengeLarge.emotion";
import { ChallengeProgressBar } from "../../challenges/ChallengeProgressBar/ChallengeProgressBar.emotion";
import type { BaseGetSectionItemProps } from "./Section.emotion";

const progressBarContainerStyle: Interpolation = css({
  top: spacing.m,
  right: spacing.m,
  width: "60px",
  [cssMediaQueryMinWidthTablet]: {
    top: spacing.l,
    right: spacing.l,
    width: "120px",
  },
});

const checkmarkIconStyle: Interpolation = css({
  top: spacing.m,
  right: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    top: spacing.l,
    right: spacing.l,
  },
});

const tagsContainerStyle: Interpolation = css({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xxs,
});

/**
 * TODO: JoinButton
 * TODO: MediaPlayer
 */
export type GetSectionItemChallengePropsProps = BaseGetSectionItemProps<
  ChallengeSectionItem | ChallengeItemSectionItem | CollectionSectionItem
>;

const getTags = (
  collectionData: SectionItemCollection,
  t: ReturnType<typeof useTranslations>,
): ReactNode[] => {
  if (collectionData.type === "challenge") {
    const usersJoinedLabel = getUsersCountLabel(collectionData, t);
    if (usersJoinedLabel) {
      return [
        <div css={tagsContainerStyle}>
          {collectionData.has_joined ? <CheckmarkIcon.Inline /> : null}
          <span>{usersJoinedLabel}</span>
        </div>,
      ];
    }
  }
  return [];
};

const getImgContainerChildren = (collectionData: SectionItemCollection) => {
  const challengeStatus = getChallengeStatus(
    collectionData.available_at,
    collectionData.ends_at,
  );
  if (collectionData.is_completed) {
    return <ChallengeCheckmark css={checkmarkIconStyle} />;
  }
  if (challengeStatus !== "pending" && collectionData.has_joined) {
    return (
      <ChallengeProgressBar
        progress={collectionData.progress || 0}
        css={progressBarContainerStyle}
      />
    );
  }
  return null;
};

export const getSectionItemChallengeProps = ({
  item,
  link,
  mutate,
  t,
  analytics,
}: GetSectionItemChallengePropsProps): ChallengeLargeProps => {
  const collectionData =
    item.reference_type === "challenge" || item.reference_type === "collection"
      ? item.reference
      : item.reference.collection;

  const actions = [
    mutate ? (
      <ChallengeCTAButton
        collection={collectionData}
        sectionId={item.item_id}
        key="cta"
      />
    ) : null,
    link ? (
      <Button
        asChild
        key="link"
        variant="lighten"
        onClickCapture={() => {
          analytics.track({
            event: HallowAnalyticsEvent.TappedDetails,
            properties: {
              section_item_id: item?.item_id ?? null,
              collection_id: collectionData.id,
            },
          });
        }}
      >
        <Link href={`/collections/${collectionData.id}`}>
          {t("web_details")}
        </Link>
      </Button>
    ) : null,
  ];

  const centerActions = [
    mutate ? (
      <ChallengeCTAButton
        collection={collectionData}
        sectionId={item.item_id}
        key="cta"
      />
    ) : null,
  ];

  return {
    actions,
    centerActions,
    asChild: link,
    availableAt:
      collectionData.type === "challenge" ? collectionData.available_at : null,
    children: link ? <Link href={`/collections/${collectionData.id}`} /> : null,
    color: collectionData.images?.color_hex as ChallengeLargeProps["color"],
    description: collectionData?.short_desc ?? collectionData?.desc,
    endsAt: collectionData?.ends_at,
    imgBlur:
      collectionData.type === "challenge" ? !collectionData.has_joined : false,
    imgSrc: collectionData?.images,
    subscription: !collectionData?.has_access,
    title: collectionData?.title,
    tags: getTags(collectionData, t),
    imgContainerProps: {
      children: getImgContainerChildren(collectionData),
    },
  };
};
